import React, { useState, useEffect } from 'react';
import './AllSetsProductionMaterialsDailyReport.css';
import api from '../api/client';

function AllSetsProductionMaterialsDailyReport({ isOpen, onClose, date, products, reportData }) {
  const [masterBagsData, setMasterBagsData] = useState([]);
  const [smallPacketsData, setSmallPacketsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noCount, setNoCount] = useState(0);

  useEffect(() => {
    if (isOpen && date && products.length > 0) {
      if (reportData) {
        // Use pre-fetched data
        setMasterBagsData(reportData.masterBags || []);
        setSmallPacketsData(reportData.smallPackets || []);
        setLoading(false);
        setError(null);
        // Count "No" occurrences
        const noOccurrences = [
          ...(reportData.masterBags || []).map(getRowData),
          ...(reportData.smallPackets || []).map(getRowData)
        ].reduce((count, row) => row.correct === 'No' ? count + 1 : count, 0);
        setNoCount(noOccurrences);
      } else {
        // Fallback to fetch if no pre-fetched data
        const fetchData = async () => {
          setLoading(true);
          setError(null);
          try {
            const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            const url = `/api/sets_materials_wright_off?prodDate=${formattedDate}`;
            console.log('Fetching sets materials report from:', url);
            const response = await api.get(url);
            const result = response.data;
            // Assuming result has { masterBags: [], smallPackets: [] }
            setMasterBagsData(result.masterBags || []);
            setSmallPacketsData(result.smallPackets || []);
            // Count "No" occurrences
            const noOccurrences = [
              ...(result.masterBags || []).map(getRowData),
              ...(result.smallPackets || []).map(getRowData)
            ].reduce((count, row) => row.correct === 'No' ? count + 1 : count, 0);
            setNoCount(noOccurrences);
          } catch (err) {
            console.error('Error fetching sets materials report:', err);
            setError(err.response ? `HTTP error! Status: ${err.response.status}` : err.message);
          } finally {
            setLoading(false);
          }
        };
        fetchData();
      }
    }
  }, [isOpen, date, products, reportData]);

  if (!isOpen) return null;

  const masterColumns = [
    { label: 'Set', key: 'set' },
    { label: 'Before', key: 'before' },
    { label: 'Used', key: 'used' },
    { label: 'Now', key: 'now' },
    { label: 'Should Be', key: 'shouldBe' },
    { label: 'Correct', key: 'correct' },
  ];

  const smallColumns = [
    { label: 'Small Bag', key: 'smallBag' },
    { label: 'Before', key: 'before' },
    { label: 'Used', key: 'used' },
    { label: 'Produced', key: 'produced' },
    { label: 'Now', key: 'now' },
    { label: 'Should Be', key: 'shouldBe' },
    { label: 'Correct', key: 'correct' },
  ];

  const getRowData = (row) => ({
    ...row,
    before: row.before ?? 0,
    used: row.used ?? 0,
    now: row.now ?? 0,
    shouldBe: row.shouldBe ?? 0,
    produced: row.produced ?? 0,
    correct: (row.now === row.shouldBe) ? 'Yes' : 'No'
  });

  return (
    <div className="all-sets-production-materials-daily-report-overlay">
      <div className="all-sets-production-materials-daily-report">
        <div className="all-sets-production-materials-daily-report-header">
          <h3>Materials Wright Off Report (sets) - Date: {date?.toLocaleDateString()}</h3>
          <button className="all-sets-production-materials-daily-report-close-button" onClick={onClose}>×</button>
        </div>
        <div className="all-sets-production-materials-daily-report-content">
          {loading && <p>Loading...</p>}
          {error && <p>Error: {error}</p>}
          {!loading && !error && (
            <>
              <h4>Master Bags</h4>
              <table className="all-sets-production-materials-daily-report-table">
                <thead>
                  <tr>
                    {masterColumns.map((col, index) => <th key={index}>{col.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {masterBagsData.map((row, index) => {
                    const rowData = getRowData(row);
                    const rowClass = rowData.correct === 'No' ? 'error-row' : '';
                    return (
                      <tr key={index} className={rowClass}>
                        {masterColumns.map((col, colIndex) => (
                          <td key={colIndex} className={col.key === 'correct' && rowData.correct === 'No' ? 'error-text' : ''}>
                            {rowData[col.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* <p>Number of discrepancies (No): {noCount}</p> */}

              <h4>Small Packets</h4>
              <table className="all-sets-production-materials-daily-report-table">
                <thead>
                  <tr>
                    {smallColumns.map((col, index) => <th key={index}>{col.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {smallPacketsData.map((row, index) => {
                    const rowData = getRowData(row);
                    const rowClass = rowData.correct === 'No' ? 'error-row' : '';
                    return (
                      <tr key={index} className={rowClass}>
                        {smallColumns.map((col, colIndex) => (
                          <td key={colIndex} className={col.key === 'correct' && rowData.correct === 'No' ? 'error-text' : ''}>
                            {rowData[col.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* <p>Number of discrepancies (No): {noCount}</p> */}
            </>
          )}
          {!loading && !error && masterBagsData.length === 0 && smallPacketsData.length === 0 && <p>No data available.</p>}
        </div>
      </div>
    </div>
  );
}

export default AllSetsProductionMaterialsDailyReport;